import { Fragment, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, UserCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import Intercom from '@intercom/messenger-js-sdk';

import Footer from './Footer';
import logo from '../assets/images/logo.png';
import { verifyToken } from '../utils/utils';
import store from '../utils/store';

const user = {
  name: 'Tom Cook',
  email: 'tom@example.com',
  imageUrl:
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
};
const navigation = [
  { name: 'APIs', href: '/' },
  { name: 'Scrapers', href: '/scrapers' },
  { name: 'Developers', href: 'https://docs.unwrangle.com', current: false },
  { name: 'Help', href: 'https://help.unwrangle.com', current: false }
  // {name: 'Reviews', href: '/', current: true},
  // { name: 'Dashboard', href: '#', current: true },
  // { name: 'Team', href: '#', current: false },
  // { name: 'Projects', href: '#', current: false },
  // { name: 'Calendar', href: '#', current: false },
];

const userNavigation = [
  { name: 'Account', href: '/profile' },
  { name: 'Billing', href: '/billing' },
  { name: 'Sign out', href: '/logout' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
};

export default function Skeleton({dashboard, children}) {
  const location = useLocation();
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await verifyToken();
      } catch (error) {
        if (error.message?.includes('security token')) {
          store.clear();
          navigate('/login');
        }
      }
    };

    // Verify token on mount and when location changes
    verifyAuth();

    if (dashboard && dashboard.email) {
      Intercom({
        app_id: process.env.REACT_APP_INTERCOM_WORKSPACE,
        user_id: dashboard.id,
        name: dashboard.name || dashboard.email,
        email: dashboard.email,
        created_at: dashboard.created_at,
      });

      // Initialize Headway widget
      const HW_config = {
        selector: "#whats-new", // CSS selector where to inject the badge
        account: "7KOlY7",
        position: {
          x: 'left'
        }
      };
      if (window.Headway) {
        window.Headway.init(HW_config);
      } else {
        const script = document.createElement('script');
        script.src = "https://cdn.headwayapp.co/widget.js";
        script.async = true;
        script.onload = () => window.Headway.init(HW_config);
        document.body.appendChild(script);
      }
    }
  }, [dashboard]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Disclosure as="nav" className="bg-white border-b border-gray-200">
        {({ open }) => (
          <>
            <div className="max-w-full mx-12 px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16">
                <div className="flex">
                  <div className="flex-shrink-0 flex items-center">
                    <Link to='/'>
                      <img
                        className="block h-8 w-auto"
                        src={logo}
                        alt="Unwrangle Logo"
                      />
                    </Link>
                  </div>
                  <div className="hidden sm:-my-px sm:ml-10 sm:flex sm:space-x-8">
                    {navigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={classNames(
                          location.pathname === item.href
                            ? 'border-mypurple text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                          'inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium'
                        )}
                        aria-current={location.pathname.includes(item.href) ? 'page' : undefined}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
                <div className='flex justify-end mr-4 items-center'>
                  <div id='whats-new' className='flex items-center'>
                    <p className='text-xs text-gray-500'>What's new</p>
                  </div>
                  <div className="hidden sm:ml-6 sm:flex sm:items-center">
                    {/* <button className="bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      <span className="sr-only">View notifications</span>
                      <BellIcon className="h-6 w-6" aria-hidden="true" />
                    </button> */}

                    {/* Profile dropdown */}
                    <div>
                      <p className='text-sm text-gray-500 user-email'>{dashboard?.email || ''}</p>
                    </div>
                    <Menu as="div" className="ml-3 relative">
                      {({ open }) => (
                        <>
                          <div>
                            <Menu.Button className="max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mypurple">
                              <span className="sr-only">Open user menu</span>
                              <UserCircleIcon className="h-8 w-8 rounded-full text-gray-400 hover:text-gray-500" alt="" />
                              <ChevronDownIcon className="h-4 w-4 text-gray-400 hover:text-gray-500" alt="" />
                            </Menu.Button>
                          </div>
                          <Transition
                            show={open}
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items
                              static
                              className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                            >
                              {userNavigation.map((item) => (
                                <Menu.Item key={item.name}>
                                  {({ active }) => (
                                    <a
                                      href={item.href}
                                      className={classNames(
                                        active ? 'bg-gray-100' : '',
                                        'block px-4 py-2 text-sm text-gray-700'
                                      )}
                                    >
                                      {item.name}
                                    </a>
                                  )}
                                </Menu.Item>
                              ))}
                            </Menu.Items>
                          </Transition>
                        </>
                      )}
                    </Menu>
                  </div>
                  <div className="-mr-2 flex items-center sm:hidden">
                    {/* Mobile menu button */}
                    <Disclosure.Button className="bg-white inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      <span className="sr-only">Open main menu</span>
                      {open ? (
                        <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                      ) : (
                        <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                      )}
                    </Disclosure.Button>
                  </div>
                </div>
              </div>
            </div>

            <Disclosure.Panel className="sm:hidden">
              <div className="pt-2 pb-3 space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={classNames(
                      item.current
                        ? 'bg-purple-50 border-mypurple text-mypurple'
                        : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800',
                      'block pl-3 pr-4 py-2 border-l-4 text-base font-medium'
                    )}
                    aria-current={item.current ? 'page' : undefined}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              <div className="pt-4 pb-3 border-t border-gray-200">
                {/* <div className="flex items-center px-4">
                  <div className="flex-shrink-0">
                    <img className="h-10 w-10 rounded-full" src={user.imageUrl} alt="" />
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-800">{user.name}</div>
                    <div className="text-sm font-medium text-gray-500">{user.email}</div>
                  </div>
                  <button className="ml-auto bg-white flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    <span className="sr-only">View notifications</span>
                    <BellIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div> */}
                <div className="mt-3 space-y-1">
                  {userNavigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
      {/* <div className="flex items-center justify-center gap-x-6 bg-gray-900 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
        <p className="text-sm leading-6 text-white">
          <a href="#">
            <strong className="font-semibold">Hey there!</strong>
            <svg viewBox="0 0 2 2" className="mx-2 inline h-0.5 w-0.5 fill-current" aria-hidden="true">
              <circle cx={1} cy={1} r={1} />
            </svg>
            We have discontinued our free trial due to excess spam. Please subscribe to a paid plan to get started.
          </a>
        </p>
      </div> */}
      <div className="py-5 flex-grow">
        {/* <header className='mb-10'>
          <div className="max-w-full mx-10 px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold leading-tight text-gray-900">Review Extractor</h1>
          </div>
        </header> */}
        <main>
          <div className="max-w-full mx-10 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  )
};
